import { Check, TriangleAlert } from 'lucide-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { CARD, ON_PRIMARY, TEXT_CAPTION } from '@/components/humint/recipes'
import { cn } from '@/utils/cn'
import {
  type ProcessingStage,
  type StageReading,
  type StageStep,
  type StepStatus,
  stageReading,
} from './processing-stages'

export interface ProcessingStagesProps {
  /** Where the latest job is; null or absent on a server that names no stage. */
  stage: ProcessingStage | null | undefined
  /** The stage the job failed in, else null. */
  failedStage?: ProcessingStage | null
  /** `rail`: the four stages in a row (the recording page); `chip`: one compact status (a list row). */
  variant: 'rail' | 'chip'
  /**
   * The surface a chip sits on: `line`, a row of the HUMINT workspace (icon and word, failure in
   * the warning tone), or `pill`, the documents list's own tinted chip (failure in its
   * destructive tint). The rail has one look.
   */
  tone?: 'line' | 'pill'
  className?: string
}

const COPY = 'processingStages'

/** The copy group of a reading: what is happening, where it stopped, or that it is through. */
const copyOf = (reading: StageReading): string =>
  reading.kind === 'done' ? `${COPY}.done` : `${COPY}.${reading.kind}.${reading.at}`

// ── Rail ─────────────────────────────────────────────────────────────────────

/** The line leaving a step: lit once the step is settled, fading out of the one being worked on. */
const LINK: Record<StepStatus, string> = {
  done: 'bg-primary',
  current: 'bg-gradient-to-r from-primary to-border',
  waiting: 'bg-border',
  // Unlit: nothing went past the step that failed.
  failed: 'bg-border',
}

const LABEL: Record<StepStatus, string> = {
  done: 'text-muted-foreground',
  current: 'font-semibold text-foreground',
  waiting: 'text-muted-foreground',
  failed: 'font-semibold text-warning-foreground',
}

/** What stands at a step: a settled check, the live signal, where it stopped, or an empty ring. */
function StepMarker({ status }: { status: StepStatus }) {
  switch (status) {
    case 'done':
      return (
        <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary">
          <Check strokeWidth={3} className={cn('size-2.5', ON_PRIMARY)} />
        </span>
      )
    case 'current':
      // The one thing on the rail that moves; it stands still for reduced motion.
      return (
        <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-primary">
          <span className="size-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        </span>
      )
    case 'failed':
      return <TriangleAlert className="size-4 shrink-0 text-warning-foreground" />
    default:
      return <span className="size-4 shrink-0 rounded-full border-2 border-border" />
  }
}

/** One station of the chain: its marker with the line to the next one, and its name under it. */
function RailStep({ step, last }: { step: StageStep; last: boolean }) {
  const { t } = useTranslation()
  return (
    <li
      aria-current={step.status === 'current' ? 'step' : undefined}
      // Never narrower than its name, in any language; the lines share what is left.
      className={cn('flex min-w-fit flex-col gap-1.5', last ? 'flex-none' : 'flex-1')}
    >
      <span aria-hidden className="flex h-4 items-center gap-1.5">
        <StepMarker status={step.status} />
        {!last && <span className={cn('h-0.5 min-w-2 flex-1 rounded-full', LINK[step.status])} />}
      </span>
      <span
        className={cn('text-xs leading-4 whitespace-nowrap', !last && 'pr-2', LABEL[step.status])}
      >
        {t(`${COPY}.stages.${step.stage}`)}
        <span className="sr-only">: {t(`${COPY}.status.${step.status}`)}</span>
      </span>
    </li>
  )
}

function Rail({ reading, className }: { reading: StageReading; className?: string }) {
  const { t } = useTranslation()
  const failed = reading.kind === 'failed'
  const copy = copyOf(reading)
  const lastIndex = reading.steps.length - 1
  return (
    <section
      aria-label={t(`${COPY}.label`)}
      data-state={reading.kind}
      className={cn(CARD, 'flex flex-col gap-3 p-4', failed && 'border-warning-border', className)}
    >
      <ol className="flex items-start gap-1.5">
        {reading.steps.map((step, index) => (
          <RailStep key={step.stage} step={step} last={index === lastIndex} />
        ))}
      </ol>
      <div role={failed ? 'alert' : 'status'} className="flex min-w-0 flex-col gap-0.5">
        <h3 className="text-base font-semibold @5xl/humint:text-sm">{t(`${copy}.title`)}</h3>
        <p className="text-sm text-muted-foreground @5xl/humint:text-xs">{t(`${copy}.body`)}</p>
      </div>
    </section>
  )
}

// ── Chip ─────────────────────────────────────────────────────────────────────

type ChipTone = NonNullable<ProcessingStagesProps['tone']>

/** Each surface keeps its own palette: the workspace warns, the documents list tints. */
const CHIP_LOOK: Record<ChipTone, Record<'base' | StageReading['kind'], string>> = {
  line: {
    base: `gap-1.5 ${TEXT_CAPTION}`,
    running: 'text-muted-foreground',
    failed: 'font-medium text-warning-foreground',
    done: 'text-muted-foreground',
  },
  pill: {
    base: 'h-4 shrink-0 gap-1 rounded px-1 text-3xs font-medium',
    running: 'bg-muted text-muted-foreground',
    failed: 'bg-destructive/10 text-destructive',
    done: 'bg-success/10 text-success',
  },
}

/** Sized in em, so the chain is in scale with whatever text the chip is set in. */
const TICK: Record<StepStatus, string> = {
  done: 'w-[0.4em] bg-primary',
  // The stage being worked on is the long tick, and the only one that moves.
  current: 'w-[0.9em] animate-pulse bg-primary motion-reduce:animate-none',
  waiting: 'w-[0.4em] bg-muted-foreground/30',
  failed: 'w-[0.4em] bg-current',
}

/** The chain in small: one tick per stage. */
function Ticks({ steps }: { steps: readonly StageStep[] }) {
  return (
    <span aria-hidden className="flex shrink-0 items-center gap-[0.2em]">
      {steps.map(step => (
        <span key={step.stage} className={cn('h-[0.4em] rounded-full', TICK[step.status])} />
      ))}
    </span>
  )
}

interface ChipProps {
  reading: StageReading
  tone: ChipTone
  className?: string
}

function Chip({ reading, tone, className }: ChipProps) {
  const { t } = useTranslation()
  const look = CHIP_LOOK[tone]
  const label =
    reading.kind === 'done'
      ? t(`${COPY}.chip.done`)
      : t(`${COPY}.chip.${reading.kind}.${reading.at}`)
  return (
    <span
      data-state={reading.kind}
      // What is being made, or what still works after a failure, one hover away.
      title={t(`${copyOf(reading)}.${reading.kind === 'running' ? 'title' : 'body'}`)}
      className={cn(
        'inline-flex items-center whitespace-nowrap',
        look.base,
        look[reading.kind],
        className
      )}
    >
      {reading.kind === 'running' && <Ticks steps={reading.steps} />}
      {reading.kind === 'failed' && <TriangleAlert aria-hidden className="size-[1.1em] shrink-0" />}
      {reading.kind === 'done' && <Check aria-hidden className="size-[1.1em] shrink-0" />}
      {label}
    </span>
  )
}

/**
 * Where the processing of an audio recording stands (contract §4), as a signal chain: it waits
 * in the queue, the speech is transcribed, the transcript is indexed for search, the entities are
 * extracted. The `rail` shows the four stages in a row — settled ones lit, the one being worked
 * on the only thing that moves, a failed one marked where the job stopped with what still works
 * said under it. The `chip` is the same chain in one compact status for a list row. Every state
 * has words, never colour or motion alone. Nothing is rendered where the server named no stage:
 * the caller keeps what it showed before. Its props are plain values, so it renders again only
 * when one of them changes: a row of a long list, or the page under a moving player, skips it.
 */
export const ProcessingStages = memo(function ProcessingStages({
  stage,
  failedStage,
  variant,
  tone = 'line',
  className,
}: ProcessingStagesProps) {
  const reading = stageReading(stage, failedStage)
  if (!reading) return null
  return variant === 'rail' ? (
    <Rail reading={reading} className={className} />
  ) : (
    <Chip reading={reading} tone={tone} className={className} />
  )
})
